/** Resolve Vibe chat deliverable kind + static report URLs for the right-hand panel. */

import { resolveReportFetchUrl } from '@/lib/reportHtml';

const ABAP_MARKERS = ['REPORT ', 'PROGRAM ', 'DATA:', 'FORM ', 'METHOD ', 'INCLUDE ', 'SELECT '];

const ABAP_REQUEST_MARKERS = [
  'abap',
  'report program',
  'alv',
  'alv grid',
  'display grid',
  'selection screen',
  'selection criteria',
  'custom program',
  'generate code',
  'write code',
  'write abap',
  'abap code',
  'se38',
  'sy-abap',
  'cl_salv',
  'salv_table',
];

/** Lightweight client hint until POST /status returns intent (mirrors backend fallback order). */
export function inferIntentFromUserMessage(text) {
  const t = String(text || '').toLowerCase();
  if (!t.trim()) return 'unclear';

  if (
    /\b(new user|provisioning|onboarding|nup|terminate|termination|offboard|ter\b|password reset|pwd reset)\b/.test(
      t
    )
  ) {
    return 'workflow';
  }
  if (
    /\b(security|authorization|su53|role assignment|add role|remove role|sap auth)\b/.test(t) &&
    !looksLikeAbapRequestText(t)
  ) {
    return 'security';
  }
  if (looksLikeAbapRequestText(t)) {
    return 'abap';
  }
  const staticHits = [
    'static report',
    'html report',
    'analytics report',
    'download link',
    'html analytics',
    'static html',
  ];
  if (staticHits.some((h) => t.includes(h))) {
    return 'static_report';
  }
  if (/(sales order report|sales orders report)/.test(t) && /\b(last|top|recent)\s+\d+\b/.test(t)) {
    return 'static_report';
  }
  const isMarkers = [
    'integration suite',
    'cloud integration',
    'cpi ',
    ' iflow',
    'i-flow',
    'api proxy',
    'value mapping',
    'integration flow',
    'sap btp integration',
  ];
  if (isMarkers.some((m) => t.includes(m))) {
    return 'integration_suite';
  }
  return 'unclear';
}

function looksLikeAbapRequestText(t) {
  const lower = String(t || '').toLowerCase();
  if (!lower.trim()) return false;
  if (lower.includes('z_')) return true;
  return ABAP_REQUEST_MARKERS.some((marker) => lower.includes(marker));
}

export function looksLikeAbapSource(text, opts = {}) {
  if (!text) return false;
  const s = String(text);
  const typeNorm = String(opts.artifactType || opts.intent || '').toLowerCase();
  const minLen =
    opts.minLength ??
    (typeNorm === 'abap' ? 48 : 400);
  if (s.length < minLen) {
    if (typeNorm === 'abap' && s.length >= 24) {
      return ABAP_MARKERS.some((m) => s.includes(m));
    }
    return false;
  }
  return ABAP_MARKERS.some((m) => s.includes(m));
}

const REPORT_FILENAME_RE = /sap_odata_report_\d{8}_\d{6}\.html/i;

/** Resolve a report filename from assistant text when artifact_url was not persisted. */
export function extractReportFilenameFromText(text) {
  const s = String(text || '');
  if (!s.trim()) return '';
  const direct = s.match(REPORT_FILENAME_RE);
  if (direct) return direct[0];
  const saved = s.match(/saved to\s+[^\s]*\/(sap_odata_report_[^\s]+\.html)/i);
  if (saved) return saved[1];
  return '';
}

export function extractReportUrlFromText(text) {
  const s = String(text || '');
  if (!s.trim()) return '';

  const labelMatch = s.match(/report link:\s*(https?:\/\/[^\s]+)/i);
  if (labelMatch) {
    return labelMatch[1].replace(/[.,)]+$/g, '');
  }

  const downloadMatch = s.match(/\*\*download link:\*\*\s*(https?:\/\/[^\s]+)/i);
  if (downloadMatch) {
    return downloadMatch[1].replace(/[.,)]+$/g, '');
  }

  const urls = s.match(/https?:\/\/[^\s)\]"']+/g);
  if (urls?.length) {
    const reportPath = urls.find((u) => /\/reports\//i.test(u));
    const chosen = reportPath || urls[urls.length - 1];
    return chosen.replace(/[.,)]+$/g, '');
  }

  const filename = extractReportFilenameFromText(s);
  if (filename) {
    return `https://local.invalid/reports/${filename}`;
  }
  return '';
}

export function isSecurityDeliverable(kind) {
  const k = String(kind || '').toLowerCase();
  return k === 'security' || k === 'sap_auth';
}

export function isWorkflowDeliverable(kind) {
  const k = String(kind || '').toLowerCase();
  return k === 'workflow' || k === 'nup' || k === 'new_user_provisioning';
}

export function isIntegrationSuiteDeliverable(kind) {
  const k = String(kind || '').toLowerCase();
  return (
    k === 'integration_suite' ||
    k === 'integration' ||
    k === 'sap_integration_suite' ||
    k === 'isuite'
  );
}

export function resolveDeliverableKind(intent, artifactType, generatedCode = null) {
  const code = String(generatedCode || '').trim();
  const typeNorm = String(artifactType || '').toLowerCase();
  const intentNorm = String(intent || '').toLowerCase();
  const abapOpts = { artifactType: typeNorm || intentNorm, intent: intentNorm };

  // Explicit artifact/intent wins over stale session-level ABAP from an earlier turn.
  if (isIntegrationSuiteDeliverable(typeNorm) || isIntegrationSuiteDeliverable(intentNorm)) {
    return 'integration_suite';
  }

  if (isSecurityDeliverable(typeNorm)) {
    if (code && looksLikeAbapSource(code, abapOpts)) {
      return 'abap';
    }
    return 'security';
  }
  if (typeNorm === 'static_report') {
    return 'static_report';
  }
  if (isWorkflowDeliverable(typeNorm)) {
    return 'workflow';
  }
  if (typeNorm === 'abap') {
    return 'abap';
  }

  if (code && looksLikeAbapSource(code, abapOpts)) {
    return 'abap';
  }

  if (isSecurityDeliverable(intentNorm)) {
    return 'security';
  }
  if (intentNorm === 'static_report') {
    return 'static_report';
  }
  if (isWorkflowDeliverable(intentNorm)) {
    return 'workflow';
  }
  if (intentNorm === 'abap') {
    return 'abap';
  }

  if (code) {
    const url = extractReportUrlFromText(code);
    if (url && (/report link:/i.test(code) || !looksLikeAbapSource(code, abapOpts))) {
      return 'static_report';
    }
    if (looksLikeAbapSource(code, abapOpts)) {
      return 'abap';
    }
    if (intentNorm === 'abap') {
      return 'abap';
    }
  }

  if (!intentNorm || intentNorm === 'unclear') {
    return 'workspace';
  }

  if (isIntegrationSuiteDeliverable(intentNorm)) {
    return 'integration_suite';
  }

  return 'abap';
}

const FALLBACK_INTENT_UI = {
  workspace: {
    id: 'workspace',
    label: 'Workspace output',
    short_label: 'Workspace',
    requires_connected: false,
    skip_confirm: false,
    supports_deploy: false,
    supports_prd: false,
    artifact_type: null,
    generating_connected: 'Thinking…',
    generating_disconnected: 'Thinking…',
    revise_generating: 'Thinking…',
  },
  abap: {
    id: 'abap',
    label: 'ABAP program',
    short_label: 'ABAP',
    requires_connected: false,
    skip_confirm: false,
    supports_deploy: true,
    supports_prd: true,
    artifact_type: 'abap',
    generating_connected: 'Thinking…',
    generating_disconnected: 'Thinking…',
    revise_generating: 'Thinking…',
  },
  security: {
    id: 'security',
    label: 'Security request',
    short_label: 'Security',
    requires_connected: true,
    skip_confirm: false,
    supports_deploy: false,
    supports_prd: true,
    artifact_type: 'security',
    generating_connected: 'Thinking…',
    revise_generating: 'Thinking…',
  },
  static_report: {
    id: 'static_report',
    label: 'Static HTML report',
    short_label: 'Static report',
    requires_connected: true,
    skip_confirm: true,
    supports_deploy: false,
    supports_prd: false,
    artifact_type: 'static_report',
    generating_connected: 'Thinking…',
    revise_generating: 'Thinking…',
  },
  workflow: {
    id: 'workflow',
    label: 'New User Provisioning',
    short_label: 'NUP',
    requires_connected: true,
    skip_confirm: true,
    supports_deploy: false,
    supports_prd: false,
    artifact_type: 'workflow',
    generating_connected: 'Thinking…',
    revise_generating: 'Thinking…',
  },
  integration_suite: {
    id: 'integration_suite',
    label: 'SAP Integration Suite',
    short_label: 'Integration Suite',
    requires_connected: true,
    skip_confirm: true,
    supports_deploy: false,
    supports_prd: false,
    artifact_type: 'integration_suite',
    generating_connected: 'Thinking…',
    revise_generating: 'Thinking…',
  },
};

/** Server-provided intent metadata from GET/POST chat status (with local fallback). */
export function resolveIntentUiMeta(intent, intentMeta = null) {
  if (intentMeta && typeof intentMeta === 'object' && intentMeta.id) {
    return {
      ...intentMeta,
      generate_ack: 'Thinking…',
      revise_generating: 'Thinking…',
      generating_connected: 'Thinking…',
      generating_disconnected: 'Thinking…',
    };
  }
  const kind = resolveDeliverableKind(intent, intentMeta?.artifact_type);
  return FALLBACK_INTENT_UI[kind] || FALLBACK_INTENT_UI.abap;
}

export function deliverableLabelFromMeta(meta) {
  return meta?.label || 'This request';
}

export function intentRequiresConnected(meta) {
  return Boolean(meta?.requires_connected);
}

export function resolveReportUrl(artifactUrl, generatedCode) {
  const direct = String(artifactUrl || '').trim();
  if (direct) return direct;
  return extractReportUrlFromText(generatedCode);
}

/** Normalize API meta into panel-ready intent / type / url fields. */
export function mergeDeliverableMeta(meta = {}) {
  const intent = meta.intent ? String(meta.intent) : null;
  let artifactType = meta.artifactType ? String(meta.artifactType) : null;
  if (!artifactType) {
    const intentNorm = String(intent || '').toLowerCase();
    if (intentNorm === 'static_report') {
      artifactType = 'static_report';
    } else if (isSecurityDeliverable(intentNorm)) {
      artifactType = 'security';
    } else if (isWorkflowDeliverable(intentNorm)) {
      artifactType = 'workflow';
    } else if (isIntegrationSuiteDeliverable(intentNorm)) {
      artifactType = 'integration_suite';
    }
  }

  let artifactUrl = meta.artifactUrl ? String(meta.artifactUrl) : null;
  const generatedCode = meta.generatedCode ?? null;
  const artifactSummary = meta.artifactSummary ?? null;
  if (!artifactUrl && (artifactType === 'static_report' || intent === 'static_report')) {
    const extracted = extractReportUrlFromText(generatedCode);
    if (extracted) artifactUrl = extracted;
  }

  const panelBlocks = Array.isArray(meta.panelBlocks)
    ? meta.panelBlocks
    : Array.isArray(meta.panel_blocks)
      ? meta.panel_blocks
      : [];

  return { intent, artifactType, artifactUrl, generatedCode, artifactSummary, panelBlocks };
}

export function resolveCopyPayload(kind, generatedCode, artifactUrl) {
  const code = String(generatedCode || '').trim();
  const url = resolveReportUrl(artifactUrl, generatedCode);
  if (kind === 'static_report' && url) {
    return resolveReportFetchUrl(url) || url;
  }
  return code;
}
